<template>
  <div class="guide-make">
    <p class="guide-lead">
      The eSign Launchpad Make.com integration connects signing events to
      2,000+ apps in Make's catalog without writing code. Trigger Salesforce
      updates when a contract completes, push signed PDFs to Google Drive,
      alert your team in Slack when a signer fails KBA - all from the same
      OAuth-authorized connection. Make's visual scenario builder gives you
      conditional routing, iterators, and array aggregators out of the box,
      so a single scenario can fan out, branch, and merge across many apps
      without an external orchestration step.
    </p>

    <q-separator class="q-my-lg" />

    <h2>What is Make.com?</h2>
    <p>
      <a href="https://www.make.com" target="_blank" rel="noopener">Make</a>
      (formerly Integromat) is a no-code automation platform. A
      <strong>scenario</strong> is a workflow that connects modules across
      apps - each module is either a <strong>trigger</strong> (instant or
      polling) or an <strong>action</strong> on a connected app. Unlike a
      linear Zap, Make scenarios are visual graphs - you draw routes,
      filters, iterators, and aggregators directly, and Make executes each
      module against every bundle that flows through.
    </p>
    <p>
      The eSign Launchpad app on Make is a Private app today - browse-and-
      install will go live with a future Make Marketplace listing. Contact
      support for an invite to the Private app.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Prerequisites</h2>
    <ul>
      <li>
        A <strong>Make account</strong>. Free, Core, Pro, or Teams all
        work - the eSign Launchpad triggers are instant (webhook-driven),
        so they don't consume Make's polling-operations quota.
      </li>
      <li>
        An <strong>eSign Launchpad portal account</strong> with permission
        for the resources you intend to automate. Connection happens via
        OAuth, so the scenario inherits the connecting user's role and
        tenant. A read-only user can subscribe to triggers and run search
        modules; only Senders + higher can run create-style actions like
        <em>Send Package From Template</em>, <em>Void Package</em>, or
        <em>Share Signed Artifact</em>.
      </li>
      <li>
        The <strong>Business tier subscription</strong> (or higher) on your
        eSign Launchpad tenant. Make traffic counts as API access, which is
        a Business-tier feature, and webhooks are gated to Business+ as well.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Connecting Make to your account</h2>
    <p>
      The first time you add an eSign Launchpad module to a scenario, Make
      prompts for authentication. The flow is standard OAuth 2.0 with PKCE:
    </p>
    <ol>
      <li>Click <strong>Create a connection</strong> on the eSign Launchpad module.</li>
      <li>
        Make redirects you to <code>www.esignlaunchpad.com/oauth/consent</code>
        with the requested scope set displayed.
      </li>
      <li>
        Review the scopes - each one is described in plain language, e.g.
        <em>"View your signing packages - read package details, signers,
        status, and signed PDFs."</em>
      </li>
      <li>
        Click <strong>Allow</strong>. Make receives an authorization code
        and exchanges it for an access token + refresh token.
      </li>
      <li>
        The connection appears in
        <a href="https://www.make.com/en/help/connections" target="_blank" rel="noopener">Make &rarr; Connections</a>
        and can be reused across any scenario.
      </li>
    </ol>
    <p>
      Refresh tokens rotate on every refresh. If the integration adds new
      scopes in a future version (e.g. when <code>sign.package.update</code>
      shipped to enable the Import Document module), Make will prompt for
      re-authentication on first run of any scenario using the new
      capability - your old connection keeps working for the existing
      scope set.
    </p>
    <p>
      <strong>Note for advanced users:</strong> Make's connector registers
      both <code>integromat.com</code> and <code>make.com</code> OAuth
      redirect URIs because Make's edge layer rewrites the legacy
      Integromat domain to the current Make domain mid-flow. Both are on
      our allowlist; you don't need to do anything special. If you ever see
      a "redirect URI mismatch" error during consent, contact support.
    </p>
    <p>
      You can revoke the integration at any time from
      <a href="/Settings/ConnectedApps">Settings &rarr; Connected Apps</a> on
      eSign Launchpad. Disconnect there immediately invalidates Make's access
      and refresh tokens; running scenarios will start failing on next event
      delivery and prompt re-authorization.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Triggers</h2>
    <p>
      Triggers are the modules that start a scenario. The eSign Launchpad
      app ships both <strong>instant triggers</strong> (webhook-driven,
      sub-second latency, no operations-quota consumption) and a small set
      of <strong>polling triggers</strong> for catch-up workflows.
    </p>

    <h3>Package lifecycle triggers (instant)</h3>
    <ul>
      <li><strong>Watch Package Sent</strong> - fires when a package is dispatched for signing.</li>
      <li><strong>Watch Package Completed</strong> - every signer has finished and the signed PDFs are available.</li>
      <li><strong>Watch Package Declined</strong> - any signer declined; no further signing can occur.</li>
      <li><strong>Watch Package Voided</strong> - sender voided or cancelled an in-flight package.</li>
      <li><strong>Watch Package Expired</strong> - the package's expiration time elapsed before completion.</li>
    </ul>

    <h3>Per-signer triggers (instant)</h3>
    <p>
      Per-signer events fire once per signer. A 5-signer package sending to
      every signer simultaneously will fire <em>Watch Signer Viewed</em> up to
      5 times. Use these for per-contact CRM updates without waiting for the
      whole package to complete.
    </p>
    <ul>
      <li><strong>Watch Signer Viewed</strong> - engagement signal that confirms notifications landed in the inbox.</li>
      <li><strong>Watch Signer Completed</strong> - an individual signer finished their portion.</li>
      <li><strong>Watch Signer Authenticated</strong> - signer passed an authentication challenge (Email OTP, SMS OTP, KBA, ID Verification). Per-signer + per-method.</li>
      <li><strong>Watch Signer Authentication Failed</strong> - auth attempt failed. Carries an attempt counter and an <code>isTerminal</code> flag (signer locked out).</li>
    </ul>

    <h3>Share-link triggers (instant)</h3>
    <p>
      The <em>Share Signed Artifact</em> action mints recipient-email-gated
      share URLs. These triggers close the loop on whether the recipient
      actually received the artifact - a signal no other eSignature Make
      integration exposes.
    </p>
    <ul>
      <li><strong>Watch Share Link Accessed</strong> - recipient clicked through to the landing page (before the OTP challenge). Lighter engagement signal.</li>
      <li><strong>Watch Share Link Redeemed</strong> - recipient OTP'd through and downloaded the artifact.</li>
    </ul>

    <h3>Polling triggers</h3>
    <p>
      Polling triggers run on Make's standard schedule (default every 15
      minutes; configurable per scenario). Use these when you'd rather Make
      pull the data on a cadence than have it pushed via webhook.
    </p>
    <ul>
      <li><strong>List Recent Packages</strong> - paginated list of packages, deduped on package id.</li>
      <li><strong>List Signers for Package</strong> - paginated signer list for one package.</li>
      <li><strong>List Templates</strong> - active templates in your tenant.</li>
    </ul>
    <q-banner class="bg-blue-1 text-blue-10 q-mb-md" rounded>
      <template v-slot:avatar>
        <q-icon name="lightbulb" color="blue-10" />
      </template>
      <strong>Quick tip - if Run Once shows "You must first choose from
      which point...":</strong>
      Right-click the trigger module (the cylinder) and pick <strong>"Choose
      where to start"</strong>. Select one of <em>All</em> (process every
      existing record once), <em>From now on</em> (only new records),
      <em>Since specific date</em>, or <em>Choose manually</em>. This setting
      is hidden in the right-click menu, not in the module's config dialog.
      Make's UI requires this on every new polling-trigger scenario; it's
      not specific to eSign Launchpad.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Actions</h2>

    <h3>Send Package From Template</h3>
    <p>
      Pick one of your saved templates from a dynamic dropdown. The module
      reads the template's role schema and renders Name + Email fields per
      role automatically. One module - creates and sends in a single step.
    </p>

    <h3>Share Signed Artifact</h3>
    <p>
      Mint a recipient-email-gated download URL for a signed document, the
      full package (ZIP), the evidence pack, or the certificate of
      completion. Pipe the URL into Gmail / Outlook / Slack / a CRM note -
      recipient enters a one-time code from their email before the file
      becomes available.
    </p>
    <p>
      <strong>Recommended for distributing artifacts</strong> - see
      "Choosing the right action" below for why this beats attaching the
      file directly in most cases.
    </p>

    <h3>Download Signed Documents</h3>
    <p>
      Fetch all signed documents for a completed package as a ZIP, ready to
      pipe into Google Drive / Dropbox / SharePoint / S3 via Make's
      file-aware "Upload File" modules. Make holds the bytes in the scenario
      bundle for the duration of the run.
    </p>

    <h3>Void Package</h3>
    <p>
      Terminate an in-flight package (Sent / Viewed / In Progress). Signers
      who haven't completed are notified via email. Common error-handling-
      branch action - for example, "if the deal stage changes to Closed Lost
      after we've already sent, void the package."
    </p>

    <h3>Resend Notification to Signer</h3>
    <p>
      Re-send the signing-notification email to one specific pending signer.
      Useful for chasing a holdout without prompting every signer on the
      package. Server-side rate-limited to 5 resends per signer per minute.
    </p>

    <h3>Get URL for Embedded Signer</h3>
    <p>
      Generate a single-use, time-limited signing URL for a signer that was
      created with a Client User ID (the embedded-signing pattern). Drop the
      URL into Slack, an iframe, a CRM note. Standard email-link signers
      will return an error here - for those, use <em>Resend Notification to
      Signer</em> instead.
    </p>

    <h3>Download Audit Trail</h3>
    <p>
      Fetch the package's full audit trail (every signer view, signature,
      auth challenge, system event, cryptographic chain hash) as PDF, JSON,
      or CSV. Optional IP-address redaction for counterparty deliveries that
      fall under privacy regulations forbidding IP exchange.
    </p>

    <h3>Make an API Call</h3>
    <p>
      Universal escape hatch. Hits any eSign Launchpad REST endpoint that
      the connection's scopes permit, with a method + path + body of your
      choosing. Use it for endpoints that don't yet have a dedicated module
      - the response is parsed as JSON and exposed as scenario bundle data.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Searches</h2>
    <p>
      Search modules look up existing data without modifying it. The
      canonical use is "trigger fires from another app, search resolves the
      matching eSign Launchpad record, then act on it."
    </p>

    <h3>Find Package</h3>
    <p>
      Look up packages by external reference id, status, or search term.
      Returns up to 10 matches sorted by most-recently-updated. Refuses to
      run with no filter - an unfiltered list-all is almost never what you
      want and would silently pick the wrong package.
    </p>

    <h3>Get Package by ID</h3>
    <p>
      Direct GUID lookup. Returns the latest server-side details - status,
      signer counts, current step, payment state.
    </p>

    <h3>Get Signer Form Field Entries</h3>
    <p>
      Returns the values a signer typed into the document's form fields
      (text fields, checkboxes, dropdowns, dates). One Make bundle per
      filled field. Pipe into Salesforce / HubSpot / Airtable / Google
      Sheets to sync collected form data downstream of a completed signing -
      closes the canonical "package completed &rarr; write fields back to
      CRM" scenario with no custom-code step required.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Common scenario recipes</h2>

    <div class="guide-example">
      <strong>"Save signed contracts to Google Drive when complete."</strong><br>
      <em>Trigger:</em> Watch Package Completed &rarr;
      <em>Action:</em> Download Signed Documents &rarr;
      <em>Action:</em> Google Drive: Upload a File
    </div>

    <div class="guide-example">
      <strong>"Post a Slack alert when a contract is sent."</strong><br>
      <em>Trigger:</em> Watch Package Sent &rarr;
      <em>Action:</em> Slack: Create a Message (with package name + external
      reference ID + sent-at timestamp)
    </div>

    <div class="guide-example">
      <strong>"Add a Google Sheets row for each completed signer."</strong><br>
      <em>Trigger:</em> Watch Signer Completed &rarr;
      <em>Search:</em> Get Signer Form Field Entries &rarr;
      <em>Iterator:</em> over the filled fields &rarr;
      <em>Action:</em> Google Sheets: Add a Row (one row per filled field, or
      use an Array Aggregator first for one row per signer with all fields
      as columns)
    </div>

    <div class="guide-example">
      <strong>"Send a download link via SMS when a contract is completed."</strong><br>
      <em>Trigger:</em> Watch Package Completed &rarr;
      <em>Action:</em> Share Signed Artifact (Scope: Evidence Pack, recipient
      email = customer's email) &rarr;
      <em>Action:</em> Twilio: Send a Message (body includes the share URL)
    </div>

    <div class="guide-example">
      <strong>"Alert ops when a signer fails KBA."</strong><br>
      <em>Trigger:</em> Watch Signer Authentication Failed &rarr;
      <em>Filter:</em> only continue if isTerminal is true &rarr;
      <em>Action:</em> Slack / Email: notify the deal owner with signer
      details and package GUID so they can reach out manually.
    </div>

    <div class="guide-example" v-pre>
      <strong>"Void the package if the upstream CRM deal closes lost."</strong><br>
      <em>Trigger:</em> CRM: Watch Deal Stage Changed &rarr;
      <em>Filter:</em> only continue if status is Closed Lost &rarr;
      <em>Search:</em> Find Package (by external reference ID =
      <code>{{dealId}}</code>) &rarr;
      <em>Action:</em> Void Package (with reason "CRM deal closed lost")
    </div>

    <div class="guide-example">
      <strong>"Archive completed contracts + audit trail weekly."</strong><br>
      <em>Trigger:</em> Schedule (Every Friday) &rarr;
      <em>Search:</em> Find Package (status = Completed, updatedSince = last
      week) &rarr; <em>Iterator</em> &rarr;
      <em>Action:</em> Download Audit Trail &rarr;
      <em>Action:</em> SharePoint / S3: Upload File
    </div>

    <q-separator class="q-my-lg" />

    <h2>Choosing the right action for "send the artifact somewhere"</h2>
    <p>
      Two actions can deliver a signed package to a recipient. Picking the
      right one matters for both file-size limits and security posture.
    </p>
    <table class="guide-table">
      <thead>
        <tr>
          <th>Destination</th>
          <th>Use this action</th>
          <th>Why</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Email, Slack, SMS, CRM note, Notion, Airtable - anything taking a URL</td>
          <td><strong>Share Signed Artifact</strong></td>
          <td>Small URL, forwards safely (each recipient still has to verify
          their email), tenant-revocable. Avoids the file-size caps most
          messaging destinations enforce (10-25 MB).</td>
        </tr>
        <tr>
          <td>Cloud-storage upload (Drive, Dropbox, OneDrive, SharePoint, Box, S3)</td>
          <td><strong>Download Signed Documents</strong></td>
          <td>Pipes the bytes into the storage destination's "Upload a File"
          module.</td>
        </tr>
        <tr>
          <td>Long-term compliance archive</td>
          <td><strong>Download Audit Trail</strong> + <strong>Download Signed Documents</strong></td>
          <td>Preserves the cryptographic chain for independent re-verification.</td>
        </tr>
        <tr>
          <td>Both messaging + storage</td>
          <td>Two action modules in the same scenario</td>
          <td>e.g. archive to Drive AND email a download link to the client.</td>
        </tr>
      </tbody>
    </table>
    <p>
      <strong>Why the OTP gate matters.</strong> The Share Signed Artifact
      path requires the recipient to enter a 6-digit code from their email
      before the file unlocks. That's a deliberate posture, not a UX
      choice - it means a forwarded link is still safe (the new holder also
      has to control the bound inbox), the download is auditable per
      recipient, and a tenant can revoke a link if it leaks.
    </p>

    <q-separator class="q-my-lg" />

    <h2>What only we have</h2>
    <p>
      Side-by-side with DocuSign, Adobe Acrobat Sign, PandaDoc, Dropbox
      Sign, and Signaturit on Make, three eSign Launchpad capabilities have
      no direct equivalent on competitor connectors:
    </p>
    <ul>
      <li>
        <strong>Share Link Redeemed trigger</strong> closes the loop on
        artifact delivery - fires when a recipient OTP's through a share
        link and downloads. Combined with <em>Share Signed Artifact</em>,
        you get a verified-receipt audit trail without integrating a
        separate email-tracking service.
      </li>
      <li>
        <strong>Universal authentication-pass triggers</strong> fire on
        KBA, IDV, SMS OTP, and email OTP success. DocuSign exposes IDV-pass
        triggers but only on premium QES tier; we surface them on every
        plan that supports the auth method.
      </li>
      <li>
        <strong>Form-field extraction search</strong> returns each filled
        field as a separate bundle - drop directly into a CRM-create module
        without a custom-code step. Adobe Acrobat Sign has a similar
        search; nobody else does.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Make vs. Zapier - when to pick which</h2>
    <p>
      The eSign Launchpad app exposes the same surface on both platforms.
      Where they differ is the orchestration model around our modules:
    </p>
    <table class="guide-table">
      <thead>
        <tr>
          <th>Choose Make when&hellip;</th>
          <th>Choose Zapier when&hellip;</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>You need conditional routing, iterators over arrays, or array
          aggregators in the same workflow. Make's visual graph handles
          fan-out/fan-in natively.</td>
          <td>You want a linear "when X happens, do Y, then Z" automation
          and don't want to think about graph topology.</td>
        </tr>
        <tr>
          <td>Cost matters at high volume - Make's free and Core tiers ship
          more operations per dollar than Zapier's equivalent tiers.</td>
          <td>The app catalog you're connecting to is broader on Zapier
          (8,000+ apps vs. Make's 2,000+). Both cover the major CRMs,
          messaging, storage, and spreadsheets; long-tail apps tilt Zapier.</td>
        </tr>
        <tr>
          <td>You need to call an eSign Launchpad endpoint we don't yet
          have a dedicated module for. <em>Make an API Call</em> covers
          everything in scope.</td>
          <td>Your team already lives in Zapier and the friction of
          learning a new tool isn't worth the marginal capability.</td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <h2>Limits and good practice</h2>
    <ul>
      <li>
        <strong>Webhook delivery rate limits.</strong> Each tenant can have
        up to 50 webhook subscriptions. There is no per-tenant rate limit
        on webhook deliveries themselves, but Make itself rate-limits
        inbound webhooks per scenario - watch the scenario's Operations
        history if events seem dropped.
      </li>
      <li>
        <strong>Per-action server-side rate limits.</strong> Resend
        Notification is capped at 5 resends per signer per minute. Audit
        trail export is capped at 60 exports per tenant per hour. The
        standard 120 requests-per-minute API limit applies to all other
        actions.
      </li>
      <li>
        <strong>Re-authentication on scope addition.</strong> When a future
        version adds a new scope (Bulk Send From Template, currently in the
        pipeline, is one example), existing connections won't have it;
        scenarios using the new capability will prompt for re-auth on first
        run. The old scope set keeps working for existing scenarios.
      </li>
      <li>
        <strong>External reference IDs are your friend.</strong> Stamp your
        upstream system's ID on the package at create time
        (<code>externalReferenceId</code>). Every trigger payload echoes it
        back, and <em>Find Package</em> can search by it. This is the
        cleanest way to round-trip between eSign Launchpad and the
        CRM/form-builder that originated the signing request.
      </li>
      <li>
        <strong>Test with disposable email addresses.</strong> Patterns
        like <code>your-name+test1@yourdomain.com</code> let you iterate
        on scenario configuration without emailing real signers. The
        plus-suffix routes to the same inbox in most providers.
      </li>
      <li>
        <strong>Use Make's "Run once" before activating.</strong> Every
        scenario has a Run once button that fires the trigger and steps
        through the modules with the actual bundle data. Catches role-
        mapping mistakes and missing inputs before they hit production.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Troubleshooting</h2>
    <table class="guide-table">
      <thead>
        <tr>
          <th>Symptom</th>
          <th>Likely cause</th>
          <th>Fix</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>OAuth consent fails with <code>redirect_uri</code> mismatch.</td>
          <td>Either the legacy <code>integromat.com</code> or current
          <code>make.com</code> callback URI is missing from the server-
          side allowlist (very rare; both should be registered).</td>
          <td>Contact support with your scenario URL and the exact error
          text. We'll verify both URIs are registered.</td>
        </tr>
        <tr>
          <td>Trigger fires once and then stops.</td>
          <td>Make's scenario went inactive (free-tier auto-pause after
          inactivity, or you toggled it off). Webhook subscription is
          still registered server-side but Make isn't accepting the
          inbound POST.</td>
          <td>Re-activate the scenario. The webhook subscription is
          preserved on our side; no reconnection needed unless you also
          revoked the OAuth connection.</td>
        </tr>
        <tr>
          <td>Module fails with <code>ID2051: scope not allowed</code>.</td>
          <td>The connection was authorized before this module's required
          scope was added. Old token doesn't carry the new permission.</td>
          <td>Reconnect from Make &rarr; Connections &rarr; the eSign
          Launchpad connection &rarr; Reauthorize. New tokens carry the
          full current scope set.</td>
        </tr>
        <tr>
          <td>Send Package action returns <code>UNKNOWN_ROLE</code>.</td>
          <td>The Role Name you mapped doesn't match a role configured on
          the template.</td>
          <td>Open the template in eSign Launchpad. Copy the exact role
          name into the Make module; role matching is case-insensitive but
          exact-text. The error message lists every valid role on the
          template.</td>
        </tr>
        <tr>
          <td>Module returns 402 Payment Required.</td>
          <td>Tier-gated feature (e.g. webhook subscriptions on
          Free/Starter; coming-soon Bulk Send From Template). Your current
          plan doesn't include the feature.</td>
          <td>Upgrade on the
          <a href="/Settings/Subscription">Subscription page</a>, or use an
          alternate module path (e.g. switch from webhook trigger to
          polling trigger).</td>
        </tr>
        <tr>
          <td>Module returns 429 Too Many Requests.</td>
          <td>You exceeded the per-tenant rate limit (120 requests per
          minute, lower on a few specific actions).</td>
          <td>Make retries with exponential backoff automatically. For
          chronic offenders, add a Sleep module between iterations or
          increase your scenario's interval.</td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <h2>Where to go next</h2>
    <ul>
      <li>
        <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a> - the
        Make instant triggers are built on top of our webhook delivery
        infrastructure. The webhooks guide covers payload schemas,
        signature verification, and retry behavior.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>
        - full scope catalog if you ever need to grant or revoke specific
        capabilities.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>
        - required reading before using the <em>Get URL for Embedded
        Signer</em> action.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=zapier">Zapier</a> - the
        sibling integration. If Make's visual graph isn't a fit, Zapier's
        linear Zaps cover the same surface.
      </li>
      <li>
        <a href="/Docs/IntegrationGuides?topic=ai-agents">AI Agents</a> -
        if Make is too rigid for your use case, the MCP server lets AI
        assistants drive eSign Launchpad in plain language with full API
        access.
      </li>
      <li>
        <a href="/Docs/ApiReference">API Reference</a> - every Make module
        is a thin wrapper around a REST endpoint documented here.
      </li>
    </ul>
  </div>
</template>

<script>
export default {
    name: 'GuideMake'
};
</script>

<style scoped>
.guide-lead {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #334155;
}

.guide-example {
    margin: 8px 0;
    padding: 12px 14px;
    background: #f8fafc;
    border-left: 3px solid #6366F1;
    border-radius: 0 6px 6px 0;
    color: #334155;
    line-height: 1.55;
}

.guide-example strong {
    display: block;
    margin-bottom: 4px;
    color: #1e293b;
}

.guide-table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
}

.guide-table th,
.guide-table td {
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    text-align: left;
    vertical-align: top;
    line-height: 1.5;
}

.guide-table th {
    background: #f8fafc;
    color: #334155;
    font-weight: 600;
}
</style>
